import { useState, useEffect } from 'react';
import { 
  CreditCard, Calendar, CheckCircle, Clock, XCircle,
  Filter, Search, ArrowUpDown, Building, Download
} from 'lucide-react';
import { clientApiService } from '../../services/clientApi';
import { Paiement, Souscription } from '../../types/api';
import { useSearchParams } from 'react-router-dom';
import { useTexts } from '../../hooks/useTexts';
import Breadcrumb, { BreadcrumbItem } from '../../components/common/Breadcrumb';

export default function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionId = searchParams.get('subscription');
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const [payments, setPayments] = useState<Paiement[]>([]);
  const [souscriptions, setSouscriptions] = useState<Souscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Trouver la souscription filtrée
  const filteredSouscription = subscriptionId 
    ? souscriptions.find(s => s.id.toString() === subscriptionId)
    : null;

  // Fonction pour supprimer le filtre de souscription
  const removeSubscriptionFilter = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('subscription');
    setSearchParams(newSearchParams);
  };

  // Charger les données
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Charger les paiements
        const paiementsParams: any = {};
        if (subscriptionId) {
          paiementsParams.souscriptions = subscriptionId;
        }
        
        const paiementsResponse = await clientApiService.getMyPaiements(paiementsParams);
        setPayments(paiementsResponse.results);

        // Charger les souscriptions pour avoir les détails
        const souscriptionsResponse = await clientApiService.getMySouscriptions();
        setSouscriptions(souscriptionsResponse.results);

      } catch (err) {
        console.error('Erreur lors du chargement des paiements:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [subscriptionId]);

  // Filtrer et trier les paiements
  const filteredPayments = payments
    .filter(payment => 
      (payment.reference_paiement?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
      payment.mode_paiement.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(payment => 
      statusFilter === 'ALL' || payment.statut_paiement === statusFilter
    )
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date_paiement);
          bValue = new Date(b.date_paiement);
          break;
        case 'amount':
          aValue = Number(a.montant_total_paye || a.montant_paye);
          bValue = Number(b.montant_total_paye || b.montant_paye);
          break;
        case 'status':
          aValue = a.statut_paiement;
          bValue = b.statut_paiement;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const totalPaid = payments
    .filter(p => p.statut_paiement === 'VALIDE')
    .reduce((sum, p) => sum + Number(p.montant_total_paye || p.montant_paye), 0);

  const pendingPayments = payments
    .filter(p => p.statut_paiement === 'EN_ATTENTE')
    .reduce((sum, p) => sum + Number(p.montant_total_paye || p.montant_paye), 0);

  // Fonction pour obtenir les détails de la souscription
  const getSouscriptionDetails = (souscriptionId: number) => {
    const souscription = souscriptions.find(s => s.id === souscriptionId);
    return souscription;
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'VALIDE':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'EN_ATTENTE':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'REJETE':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'VALIDE':
        return 'bg-green-100 text-green-800';
      case 'EN_ATTENTE':
        return 'bg-orange-100 text-orange-800';
      case 'REJETE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'VIREMENT':
        return <CreditCard className="w-4 h-4" />;
      case 'ESPECES':
        return <CreditCard className="w-4 h-4" />;
      case 'CARTE':
        return <CreditCard className="w-4 h-4" />;
      case 'MOBILE_MONEY':
        return <CreditCard className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleDownloadFacture = async (payment: Paiement) => {
    try {
      const blob = await clientApiService.telechargerFacture(payment.id);
      
      // Créer un URL temporaire pour le blob
      const url = window.URL.createObjectURL(blob);
      
      // Créer un élément <a> temporaire pour déclencher le téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_paiement_${payment.id}_${payment.date_paiement.replace(/-/g, '')}.pdf`;
      
      // Ajouter le lien au DOM, cliquer dessus, puis le supprimer
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Libérer l'URL temporaire
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erreur lors du téléchargement de la facture:', error);
      setError('Erreur lors du téléchargement de la facture');
    }
  };

  // Préparer les éléments du fil d'Ariane
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: getText('payments.my_payments'), current: !subscriptionId }
  ];

  if (subscriptionId) {
    breadcrumbItems.unshift(
      { label: getText('nav.subscriptions'), href: '/subscriptions' },
      { label: `Souscription #${subscriptionId}`, href: `/subscriptions/${subscriptionId}` },
      { label: getText('nav.payments'), current: true }
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {subscriptionId ? getText('payments.subscription_payments') : getText('payments.my_payments')}
          </h1>
          <p className="text-gray-600 mt-2">
            {subscriptionId 
              ? getText('payments.subscription_description')
              : getText('payments.all_description')
            }
          </p>
          {subscriptionId && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Building className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-blue-800">
                        {getText('payments.filter_by_subscription')}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        ID: {subscriptionId}
                      </span>
                    </div>
                    {filteredSouscription ? (
                      <div className="text-sm text-blue-700">
                        <div className="font-medium">
                          {filteredSouscription.type_maison_designation} - {filteredSouscription.batiment_nom}
                        </div>
                        <div className="text-blue-600">
                          {filteredSouscription.projet_nom} • Niveau {filteredSouscription.niveau}
                        </div>
                        <div className="text-blue-600">
                          Prix: {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'XOF',
                            minimumFractionDigits: 0,
                          }).format(filteredSouscription.prix_convenu_ht || 0)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-blue-600">
                        {getText('payments.loading_subscription')}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={removeSubscriptionFilter}
                  className="text-blue-400 hover:text-blue-600 transition-colors"
                  title={getText('payments.remove_filter')}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">{getText('payments.loading')}</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className='text-sm text-gray-600'>{getText('payments.total_paid')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalPaid.toLocaleString()} FCFA
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className='text-sm text-gray-600'>{getText('payments.pending')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {pendingPayments.toLocaleString()} FCFA
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className='text-sm text-gray-600'>{getText('payments.count')}</p>
                  <p className="text-2xl font-bold text-gray-900">{payments.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className='text-sm text-gray-600'>{getText('payments.last_date')}</p>
                  <p className="text-lg font-bold text-gray-900">
                    {payments.length > 0 ? 
                      new Date(Math.max(...payments.map(p => new Date(p.date_paiement).getTime())))
                        .toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) 
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {!loading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={getText('payments.search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="relative">
                <Filter className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value='ALL'>{getText('payments.all_statuses')}</option>
                  <option value='VALIDE'>{getText('payments.validated')}</option>
                  <option value='EN_ATTENTE'>{getText('payments.pending')}</option>
                  <option value='REJETE'>{getText('payments.rejected')}</option>
                </select>
              </div>
              
              <div className="flex gap-2">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="date-desc">Plus récent</option>
                  <option value="date-asc">Plus ancien</option>
                  <option value="amount-desc">Montant décroissant</option>
                  <option value="amount-asc">Montant croissant</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Payments Table */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">
                        Date
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Référence
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center">
                        Montant
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mode
                    </th>
                    {!subscriptionId && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Souscription
                      </th>
                    )}
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Statut
                        <ArrowUpDown className="ml-1 w-4 h-4" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((payment) => {
                    return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {new Date(payment.date_paiement).toLocaleDateString('fr-FR')}
                              </div>
                              <div className="text-sm text-gray-500">
                                {new Date(payment.date_paiement).toLocaleDateString('fr-FR', { 
                                  weekday: 'long' 
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {payment.reference_paiement || 'N/A'}
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-lg font-bold text-gray-900">
                            {Number(payment.montant_total_paye || payment.montant_paye).toLocaleString('fr-FR')} FCFA
                          </div>
                          {payment.repartitions && payment.repartitions.length > 1 && (
                            <div className="text-xs text-gray-500 mt-1">
                              Réparti sur {payment.repartitions.length} souscription{payment.repartitions.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getModeIcon(payment.mode_paiement)}
                            <span className="ml-2 text-sm text-gray-900">
                              {payment.mode_paiement.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        
                        {!subscriptionId && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 text-gray-400 mr-2" />
                              <div>
                                {payment.repartitions && payment.repartitions.length > 0 ? (
                                  <div>
                                    {payment.repartitions.length === 1 ? (
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          Souscription #{payment.repartitions[0].souscription}
                                        </div>
                                        {(() => {
                                          const souscriptionDetails = getSouscriptionDetails(payment.repartitions[0].souscription);
                                          return souscriptionDetails && (
                                            <div className="text-sm text-gray-500">
                                              {souscriptionDetails.date_souscription ? 
                                                new Date(souscriptionDetails.date_souscription).toLocaleDateString('fr-FR') 
                                                : 'N/A'
                                              }
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {payment.repartitions.length} souscriptions
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          Multi-souscriptions
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      Souscription #{payment.souscription || 'N/A'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      Ancien système
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(payment.statut_paiement)}
                            <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.statut_paiement)}`}>
                              {payment.statut_paiement.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {payment.facture_pdf && payment.statut_paiement === 'VALIDE' && (
                              <button
                                onClick={() => handleDownloadFacture(payment)}
                                className="text-blue-600 hover:text-blue-900 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                                title="Télécharger la facture"
                              >
                                <Download className="w-4 h-4" />
                                <span>Télécharger la facture</span>
                              </button>
                            )}
                            {!payment.facture_pdf && payment.statut_paiement === 'VALIDE' && (
                              <span className="text-gray-400 text-xs">
                                Facture en cours
                              </span>
                            )}
                            {payment.statut_paiement !== 'VALIDE' && (
                              <span className="text-gray-400 text-xs">
                                Non disponible
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredPayments.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {subscriptionId ? getText('payments.empty.subscription.title') : getText('payments.empty.title')}
            </h3>
            <p className="text-gray-600 mb-6">
              {subscriptionId 
                ? getText('payments.empty.subscription.description')
                : getText('payments.empty.description')
              }
            </p>
            {!subscriptionId && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setSortBy('date');
                  setSortOrder('desc');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {getText('payments.reset_filters')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}